import { useMemo } from "react";
import useUserProfileStore from "../zustandStores/userProfileStore";
import { get50PerUsersCheck, getPercentageUsersCheck } from "../services/utils";
import AB_TEST_CONFIG from "../config/abTestConfig";

/**
 * Custom hook for A/B testing
 *
 * @param testName - The name of the A/B test from AB_TEST_CONFIG
 * @returns Object containing A/B test state and configuration
 */
export const useABTest = (testName: string) => {
  const { profileData } = useUserProfileStore();

  const result = useMemo(() => {
    const recruiterId = profileData?.recruiter_id;
    const config = AB_TEST_CONFIG[testName];

    // If test is not configured or disabled, return control group
    if (!config || !config.enabled) {
      return {
        isVariant: false,
        recruiterId,
        config: config || null
      };
    }
    // Determine if user should see variant based on recruiter ID
    // Use percentage-based check if rolloutPercentage is configured
    const isVariant = config.rolloutPercentage
      ? getPercentageUsersCheck(recruiterId, config.rolloutPercentage)
      : get50PerUsersCheck(recruiterId) || false;

    return {
      isVariant,
      recruiterId,
      config
    };
  }, [profileData?.recruiter_id, testName]);

  return result;
};

/**
 * Specific hook for Contact Sales A/B test
 * Provides a more semantic API for this specific test
 */
export const useContactSalesABTest = () => {
  const abTest = useABTest("CONTACT_SALES");
  return {
    shouldShowV2Form: abTest.isVariant,
    recruiterId: abTest.recruiterId,
    config: abTest.config
  };
};

/**
 * Utility function to check if a specific A/B test variant should be shown
 * Can be used in non-React contexts or for simple boolean checks
 *
 * @param testName - The name of the A/B test from AB_TEST_CONFIG
 * @param recruiterId - The recruiter ID to check against
 * @returns boolean indicating if variant should be shown
 */
export const isABTestVariant = (
  testName: string,
  recruiterId?: number | string
): boolean => {
  const config = AB_TEST_CONFIG[testName];

  if (!config || !config.enabled) {
    return false;
  }

  // Use percentage-based check if rolloutPercentage is configured
  return config.rolloutPercentage
    ? getPercentageUsersCheck(recruiterId, config.rolloutPercentage)
    : get50PerUsersCheck(recruiterId) || false;
};

/**
 * Specific hook for Plan Selection Sales Contact A/B test
 * 20% see SalesContactCard, 80% see SalesContact component
 */
export const usePlanSelectionSalesContactABTest = () => {
  const abTest = useABTest("PLAN_SELECTION_SALES_CONTACT");
  return {
    showSalesContactCard: abTest.isVariant, // 20% see card
    showSalesContact: !abTest.isVariant, // 80% see component
    recruiterId: abTest.recruiterId,
    config: abTest.config
  };
};

export default useABTest;
